import { Icon, Text, HStack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import {
  CompositionEntity,
  CostSimulatorEntity,
  HighScenarioEntity,
  LineEntity,
  TableEntity,
} from '@genial/services';
import { Pressable } from 'native-base';
import React from 'react';

import { StructuredOperationCompositionSection } from '../StructuredOperationCompositionSection';
import { StructuredOperationCostSimulatorSection } from '../StructuredOperationCostSimulatorSection';
import { StructuredOperationInvestmentSimulatorSection } from '../StructuredOperationInvestmentSimulatorSection';
import { StructuredOperationOperationSection } from '../StructuredOperationOperationSection';

export interface OperationSectionProps {
  scenarios?: HighScenarioEntity[];
  table?: TableEntity;
}
export interface CompositionSectionProps {
  composition?: CompositionEntity;
  operation?: string;
}
export interface InvestmentSimulatorSectionProps {
  investmentSimulatorChartData?: LineEntity[];
  marketIsOpened?: boolean;
  minValue?: number;
  onClose?: () => void;
}
export interface InvestmentCostSectionProps {
  marketIsOpened?: boolean;
  minValue?: number;
  costSimulator?: CostSimulatorEntity;
  onClose?: () => void;
}

type StructuredOperationSubDetailsSectionProps = OperationSectionProps &
  CompositionSectionProps &
  InvestmentSimulatorSectionProps &
  InvestmentCostSectionProps & {
    type: 'operation' | 'composition' | 'investment-simulator' | 'cost-simulator';
    title: string;
    isOpened: boolean;
    onOpen: () => void;
  };

export const StructuredOperationSubDetailsSection: React.FC<
  StructuredOperationSubDetailsSectionProps
> = ({ type, title, isOpened, onOpen, ...props }) => {
  const iconName = isOpened ? 'ChevronUp' : 'ChevronDown';

  const openedComponent = {
    operation: (
      <StructuredOperationOperationSection scenarios={props?.scenarios} table={props?.table} />
    ),
    composition: (
      <StructuredOperationCompositionSection
        composition={props?.composition}
        operation={props?.operation}
      />
    ),
    'investment-simulator': (
      <StructuredOperationInvestmentSimulatorSection
        minValue={props?.minValue}
        investmentSimulatorChartData={props?.investmentSimulatorChartData}
        marketIsOpened={props?.marketIsOpened}
        onClose={props?.onClose}
      />
    ),
    'cost-simulator': (
      <StructuredOperationCostSimulatorSection
        minValue={props?.minValue}
        marketIsOpened={props?.marketIsOpened}
        costSimulator={props?.costSimulator}
        onClose={props?.onClose}
      />
    ),
  };

  return (
    <VStack borderRadius={BoxRadius.sm} bg={'background'} p={Spacing['inline.xxxs']}>
      <Pressable w={'full'} onPress={onOpen}>
        <HStack w={'full'} alignItems={'center'} justifyContent={'space-between'}>
          <Text
            flex={1}
            color={'neutral.black'}
            variant="semibold"
            fontSize={Typography['fontsize.md']}>
            {title}
          </Text>
          <Icon
            testID="chevron-icon"
            name={iconName}
            size={'xxs'}
            color={Color['brand.primary.principal']}
          />
        </HStack>
      </Pressable>
      {isOpened && openedComponent[type]}
    </VStack>
  );
};
